import { useRef, useState } from "react";

import { Box, Image } from "@chakra-ui/react";
import { useGetIndividualVaultSavings } from "api/individual-savings/individual-savings-api";
import {
  FIRST_INDEX,
  vaultSavingsColumns,
} from "features/individuals-savings/consts";
import { EmptyImagePlaceholder } from "features/individuals/individuals/individuals-list-table/EmptyImagePlaceholder";
import { individualTableColumnCss } from "features/individuals/individuals/styles";
import { useTableActions } from "hooks/useTableActions";
import {
  capitalizeFirstLetter,
  convertDaysToMonthsAndDays,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination, { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { Status } from "ui/status";
import { TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";

import { IndividualsSavingsStats } from "./IndividualsSavingsStats";
import { TableHeaderCTAs } from "./TableHeaderCTAs";
import { MoreQuickActions } from "./more-quick-actions";

export const getTransationStatus = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "success";
  } else if (Savingstatus === "paused" || Savingstatus === "stopped") {
    Savingstatus = "error";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "blue";
  } else {
    Savingstatus = "disabled";
  }

  return Savingstatus;
};

export const getTransationStatusText = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "Active";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "Completed";
  } else if (Savingstatus === "deleted" || Savingstatus === "stopped") {
    Savingstatus = "Stopped";
  } else {
    Savingstatus = capitalizeFirstLetter(Savingstatus);
  }

  return Savingstatus;
};

const VaultSavings = () => {
  const {
    isLoading: isLoadingVaultSavings,
    data,
    refetch,
    isRefetching,
  } = useGetIndividualVaultSavings();
  const [selectedBusinessIds, setSelectedBusinessIds] =
    useState<TSelectedTableRowIds>({});

  const {
    handleRefreshTable,
    searchQuery,
    handleSearchQueryChange,
    isRefreshing,
  } = useTableActions({
    isLoadingTable: isLoadingVaultSavings,
    refetch,
  });
  const { data: individualVaultSavings, ...paginationData } = data?.data || {
    data: [],
    ...DEFAULT_PAGINATION_DATA,
  };
  const tableRef = useRef<HTMLTableElement | null>(null);
  const formattedData = individualVaultSavings.map((saving) => {
    const {
      id,
      email,
      maturityDate,
      lockDuration,
      name,
      status,
      firstName,
      lastName,
      interestToEarn,
      amount,
      profileImage,
    } = saving;
    const fullName = `${firstName} ${lastName}`;

    return {
      id,
      "Saver's Name": (
        <Box className="flex gap-3 items-center">
          {profileImage ? (
            <Image
              className="w-[32px] h-[32px] rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              src={profileImage}
              alt={`${fullName}'s image`}
            />
          ) : (
            <EmptyImagePlaceholder
              className="w-[32px] h-[32px] uppercase text-xs font-semibold rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              initial={fullName?.[FIRST_INDEX]}
              index={id as string}
            />
          )}
          <Box css={individualTableColumnCss}>
            <Box className="text-sm leading-[14px] tracking-[0%] mb-1 font-semibold text-[#09090B] capitalize">
              {fullName}
            </Box>
            <Box className="font-medium text-xs tracking-[0%] leading-[12px] text-[#718096]">
              {email}
            </Box>
          </Box>
        </Box>
      ),
      "Vault Name": <Box css={individualTableColumnCss}>{name || "N/A"}</Box>,
      "Amount Saved": (
        <Box css={individualTableColumnCss}>
          {handleAmountFormat({
            value: String(amount / ONE_NAIRA_IN_KOBO),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </Box>
      ),
      "Interest Earned": (
        <Box css={individualTableColumnCss}>
          {handleAmountFormat({
            value: String(interestToEarn / ONE_NAIRA_IN_KOBO),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </Box>
      ),
      "Maturity Date": (
        <Box css={individualTableColumnCss}>
          {maturityDate ? formatDateToDDMMMYYYY(maturityDate) : "N/A"}
        </Box>
      ),
      Duration: (
        <Box css={individualTableColumnCss}>
          {convertDaysToMonthsAndDays(lockDuration)}
        </Box>
      ),
      Status: (
        <Status
          status={getTransationStatus(status) as TStatus}
          text={getTransationStatusText(status)}
          className="w-fit"
        />
      ),
      "": <MoreQuickActions individualsSavingDetails={saving} />,
    };
  });

  return (
    <div>
      <Box>
        <IndividualsSavingsStats />
      </Box>
      <TableWrapper className="!p-0">
        <TableHeaderCTAs
          handleRefreshTable={handleRefreshTable}
          handleSearchQueryChange={handleSearchQueryChange}
          isRefreshing={isRefreshing}
          searchQuery={searchQuery}
        />
        <Table
          columns={vaultSavingsColumns}
          data={formattedData || []}
          tableRef={tableRef}
          hasCheckbox
          isLoading={isLoadingVaultSavings && !isRefetching}
          selectedTableRowIds={selectedBusinessIds}
          setSelectedTableRowIds={setSelectedBusinessIds}
        />
        <Pagination
          paginationData={paginationData}
          data={individualVaultSavings}
          defaultPaginationParams={DEFAULT_PAGINATION_DATA}
          tableRef={tableRef}
          isLoadingTable={isLoadingVaultSavings}
        />
      </TableWrapper>
    </div>
  );
};

export default VaultSavings;
